/**
 * ShopComingSoon — premium "coming soon" surface shown to anglers while the
 * marketplace / bait bundles / fishery booking partnerships are being secured.
 *
 * Public users see this in place of the live shop. Admins still see the full
 * shop / bait preview behind an admin-only tab.
 *
 * Design: black + gold cinematic, glass cards, gold-shimmer borders, lock
 * icons over blurred preview rows, and a single waitlist capture per teaser.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Lock, Sparkles, Gift, Award, MapPin, Bell, Check, Loader2, Crown, Star, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEASERS = [
  {
    interest: 'marketplace',
    label: 'Marketplace launching soon',
    title: 'Carp Cast Marketplace',
    icon: ShoppingBag,
    description:
      'A curated marketplace built specifically for carp anglers — official brand partners, verified suppliers, and member-only pricing. Launching once partner brands are confirmed.',
    cta: 'Notify me when the marketplace opens',
    accent: 'gold',
  },
  {
    interest: 'bait_bundles',
    label: 'Premium bait bundles coming soon',
    title: 'Premium Bait Subscription Boxes',
    icon: Gift,
    description:
      'Monthly bait boxes built on real UK supplier packaging — 1kg, 2kg and 3kg boilie tiers with matching pop-ups, liquids and hookbait extras. Public sign-ups open the moment our fulfilment partnership is locked in.',
    cta: 'Reserve early access',
    accent: 'orange',
  },
  {
    interest: 'brand_partnerships',
    label: 'Official brand partnerships in progress',
    title: 'Trusted UK Carp Brands',
    icon: Award,
    description:
      'We\'re finalising partnerships with leading UK carp bait and tackle brands. Once agreements are signed, you\'ll be the first to access partner-exclusive bundles, drops and pricing.',
    cta: 'Get the launch announcement',
    accent: 'gold',
  },
  {
    interest: 'fishery_bookings',
    label: 'Fishery booking partnerships coming soon',
    title: 'Book Sessions Directly From The Lake Page',
    icon: MapPin,
    description:
      'Day tickets, syndicate enquiries and overnight sessions, booked in two taps — straight from each fishery profile. Rolling out as partner fisheries come online.',
    cta: 'Tell me when bookings open',
    accent: 'green',
  },
  {
    interest: 'member_deals',
    label: 'Exclusive member deals incoming',
    title: 'Pro & Elite Member Perks',
    icon: Crown,
    description:
      'Pro and Elite members will unlock partner discounts, early-access drops and free trials of new bait launches. Already a member? You\'ll be at the front of the queue.',
    cta: 'Add me to the member list',
    accent: 'gold',
  },
];

const ACCENT_STYLES = {
  gold:   { ring: 'ring-[#c8b273]/40', glow: 'from-[#c8b273]/20', icon: 'text-[#c8b273]', dot: 'bg-[#c8b273]' },
  orange: { ring: 'ring-[#ff5e00]/40', glow: 'from-[#ff5e00]/20', icon: 'text-[#ff5e00]', dot: 'bg-[#ff5e00]' },
  green:  { ring: 'ring-[#22c55e]/40', glow: 'from-[#22c55e]/20', icon: 'text-[#22c55e]', dot: 'bg-[#22c55e]' },
};

const TeaserCard = ({ teaser, index }) => {
  const a = ACCENT_STYLES[teaser.accent] || ACCENT_STYLES.gold;
  const Icon = teaser.icon;
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('carpcast_token');
      await axios.post(
        `${API}/waitlist`,
        { email: email.trim().toLowerCase(), interest: teaser.interest },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setJoined(true);
      toast.success("You're on the list. We'll be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className={`relative overflow-hidden rounded-2xl border border-white/10 bg-[#0b0d0b] ring-1 ${a.ring} p-7 md:p-9`}
      data-testid={`teaser-${teaser.interest}`}
    >
      {/* Soft radial glow */}
      <div className={`pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-3xl`} />
      {/* Top status badge */}
      <div className="relative flex items-center gap-2 mb-5">
        <span className={`h-1.5 w-1.5 rounded-full ${a.dot} animate-pulse`} />
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#8a948a]">
          {teaser.label}
        </span>
      </div>

      <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex-1 min-w-0">
          <div className={`inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/30 ${a.icon} mb-4`}>
            <Icon className="h-5 w-5" />
          </div>
          <h3 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-extrabold uppercase tracking-wider text-white mb-2">
            {teaser.title}
          </h3>
          <p className="text-sm md:text-base text-[#a8b0a8] leading-relaxed max-w-xl">
            {teaser.description}
          </p>
        </div>

        <div className="md:w-[340px] md:shrink-0">
          {joined ? (
            <div className="rounded-xl border border-[#c8b273]/40 bg-[#c8b273]/5 px-4 py-4 flex items-start gap-3" data-testid={`teaser-joined-${teaser.interest}`}>
              <Check className="h-4 w-4 mt-0.5 text-[#c8b273] flex-shrink-0" />
              <div className="text-sm">
                <p className="font-bold uppercase tracking-widest text-[#c8b273] text-xs">You're on the list</p>
                <p className="text-[#a8b0a8] mt-1 leading-relaxed">
                  We'll email you the moment it goes live.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-2">
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
                data-testid={`teaser-email-${teaser.interest}`}
                required
              />
              <Button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full"
                data-testid={`teaser-submit-${teaser.interest}`}
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                {teaser.cta}
              </Button>
              <p className="text-[10px] text-[#6b7d6b] text-center mt-2">
                No spam. One email when it goes live.
              </p>
            </form>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const BlurredPreviewStrip = () => (
  <div className="relative overflow-hidden rounded-xl border border-white/5 bg-[#0b0d0b] mt-12">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-[#0b0d0b] p-5 blur-[2px] select-none pointer-events-none opacity-60">
          <div className="aspect-square rounded bg-gradient-to-br from-[#1a1d1a] via-[#0f110f] to-[#1a1d1a] mb-3 flex items-center justify-center">
            <Gift className="w-10 h-10 text-[#c8b273]/20" />
          </div>
          <div className="h-3 w-3/4 rounded bg-white/10 mb-2" />
          <div className="h-3 w-1/2 rounded bg-white/10" />
        </div>
      ))}
    </div>
    {/* Centered lock overlay */}
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-[#050605]/40 via-[#050605]/70 to-[#050605]/95">
      <div className="text-center">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#c8b273]/40 bg-[#c8b273]/10 mb-3">
          <Lock className="w-6 h-6 text-[#c8b273]" />
        </div>
        <p className="data-label text-[#c8b273] mb-1">Partner Brands Incoming</p>
        <p className="text-sm text-[#a8b0a8] max-w-md mx-auto px-4">
          Verified UK carp brands. Unlocked the moment partnerships are signed.
        </p>
      </div>
    </div>
  </div>
);

const TrustStrip = () => (
  <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
    {[
      { icon: Shield, title: 'Verified partners only', body: 'We only stock from brands and fisheries we have signed agreements with.' },
      { icon: Sparkles, title: 'Member-first pricing', body: 'Pro & Elite members will see partner-exclusive deals before they go public.' },
      { icon: Star,    title: 'Built by anglers',     body: 'Every bundle is reviewed against real UK session needs before it launches.' },
    ].map((t, i) => (
      <div key={i} className="rounded-xl border border-white/5 bg-[#0b0d0b] p-5">
        <t.icon className="w-5 h-5 text-[#c8b273] mb-3" />
        <p className="font-bold uppercase tracking-widest text-[10px] text-[#c8b273] mb-1">{t.title}</p>
        <p className="text-sm text-[#a8b0a8] leading-relaxed">{t.body}</p>
      </div>
    ))}
  </div>
);

const ShopComingSoon = () => {
  return (
    <div data-testid="shop-coming-soon" className="min-h-[60vh]">
      {/* Header / intro */}
      <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#c8b273]/30 bg-[#c8b273]/5 px-4 py-1.5 mb-5">
          <Sparkles className="w-3.5 h-3.5 text-[#c8b273]" />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273]">Soft Launch</span>
        </div>
        <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-4">
          Carp Cast Commerce Is On Its Way
        </h2>
        <p className="text-base md:text-lg text-[#a8b0a8] leading-relaxed">
          We're focusing on the things that matter first — community, catches, competitions and lake intel.
          The marketplace, bait bundles and direct fishery bookings will roll out as soon as we have the right
          partners locked in. Get on the early-access list below.
        </p>
      </div>

      <div className="space-y-6 md:space-y-7 max-w-5xl mx-auto">
        {TEASERS.map((t, i) => (
          <TeaserCard key={t.interest} teaser={t} index={i} />
        ))}
      </div>

      <BlurredPreviewStrip />
      <TrustStrip />

      <p className="text-center text-xs text-[#6b7d6b] mt-12">
        Carp Cast partners with brands and fisheries directly. Nothing on this page implies a partnership that has not been signed.
      </p>
    </div>
  );
};

export default ShopComingSoon;
